import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { UserRole } from 'business/definitions';
import { useClientHasModule } from 'hooks/client';
import { MODULES_TRANSLATION_PATH } from './helpers/constants';
import { ModuleTree } from './types';
import GenericSwitchBar from './ui/GenericSwitchBar';
import SimpleSubRoleCard from './SimpleSubRoleCard';
import SubRoleCardWithPermissions from './SubRoleCardWithPermissions';
import { isRoleTreeWithPermissions } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/components/UserFormGeneralSection/components/helpers/typeHelpers';

type Props = {
    'data-testid': string;
    module: ModuleTree;
    addRole: (r: UserRole) => void;
    removeRole: (r: UserRole) => void;
    roles: UserRole[];
};

const ModuleWithSubRoles = ({
    'data-testid': dataTestId,
    module,
    addRole,
    removeRole,
    roles,
}: Props): JSX.Element => {
    const { t } = useTranslation();

    const hasModule = useClientHasModule(module.name);

    if (!hasModule) return <></>;

    const isCreatedUserModuleUser = roles.includes(module.moduleRole);

    const handleSwitchChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        role: UserRole,
    ) => (event.target.checked ? addRole(role) : removeRole(role));

    return (
        <GenericSwitchBar
            checked={isCreatedUserModuleUser}
            data-testid={dataTestId}
            onChange={(event) => handleSwitchChange(event, module.moduleRole)}
            title={t(`${MODULES_TRANSLATION_PATH}.titles.${module.moduleRole}`)}
        >
            {isCreatedUserModuleUser && (
                <Grid container spacing={2}>
                    {module.roles.map((role) => (
                        <Grid
                            item
                            key={role.roleName}
                            xs={12 / module.roles.length}
                        >
                            {isRoleTreeWithPermissions(role) ? (
                                <SubRoleCardWithPermissions
                                    addRole={addRole}
                                    removeRole={removeRole}
                                    roleTree={role}
                                    userRoles={roles}
                                />
                            ) : (
                                <SimpleSubRoleCard
                                    addRole={addRole}
                                    removeRole={removeRole}
                                    roleTree={role}
                                    userRoles={roles}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>
            )}
        </GenericSwitchBar>
    );
};

export default ModuleWithSubRoles;
